import {
  Box,
  Flex,
  HStack,
  Icon,
  Image,
  Stack,
  Text,
  Button,
} from "@chakra-ui/react";
import { FaFacebookF, FaInstagram } from "react-icons/fa";
import type { CreativeBlock } from "../CreateCampaignFlow";

export type CreativeBlockReviewProps = {
  creative: CreativeBlock;
  index: number;
  onEdit: () => void;
  launching?: boolean;
};

export default function CreativeBlockReview({
  creative,
  index,
  onEdit,
  launching = false,
}: CreativeBlockReviewProps) {
  const squareFilename =
    creative.squareMediaFileKey?.split("/").pop() || "file_name";
  const verticalFilename =
    creative.verticalMediaFileKey?.split("/").pop() || "file_name";

  const imageHeight = "150px" as const;

  return (
    <Box
      bg="white"
      rounded="lg"
      p={4}
      border="1px solid"
      borderColor="gray.200"
    >
      <HStack>
        <Text fontSize="sm" color="gray.800" fontWeight="semibold" mb={4}>
          Ad {index + 1}
        </Text>
        <Text fontSize="xs" color="gray.500" mb={4}>
          Both formats required
        </Text>
      </HStack>

      <Stack direction={{ base: "column", sm: "row" }} spacing={4} mb={4}>
        <Box
          flex={1}
          aspectRatio={1}
          alignContent="center"
          w="50%"
          bg="gray.50"
          rounded="lg"
          p={2}
          border="1px dotted"
          borderColor="gray.300"
          textAlign="center"
        >
          <Text fontSize="sm" color="gray.500" fontWeight="semibold" mb={2}>
            <Text as="span" color="gray.600">
              Square{" "}
            </Text>
            1:1
          </Text>
          <Box mx="auto" w="fit-content" shadow="lg">
            {creative.squareMediaUrl ? (
              creative.squareMediaType === "video" ? (
                <Box
                  as="video"
                  src={creative.squareMediaUrl}
                  h={imageHeight}
                  aspectRatio={1}
                  objectFit="cover"
                  autoPlay
                  muted
                  loop
                  controls
                />
              ) : (
                <Image
                  src={creative.squareMediaUrl}
                  alt=""
                  h={imageHeight}
                  aspectRatio={1}
                  objectFit="cover"
                />
              )
            ) : (
              <Flex w="full" h="full" align="center" justify="center">
                <Text fontSize="xs" color="gray.400">
                  —
                </Text>
              </Flex>
            )}
          </Box>
        </Box>
        <Box
          flex={1}
          aspectRatio={1}
          alignContent="center"
          w="50%"
          bg="gray.50"
          rounded="lg"
          p={2}
          border="1px dotted"
          borderColor="gray.300"
          textAlign="center"
        >
          <Text fontSize="sm" color="gray.500" fontWeight="semibold" mb={2}>
            <Text as="span" color="gray.600">
              Vertical{" "}
            </Text>
            16:9
          </Text>
          <Box mx="auto" w="fit-content" shadow="lg">
            {creative.verticalMediaUrl ? (
              creative.verticalMediaType === "video" ? (
                <Box
                  as="video"
                  src={creative.verticalMediaUrl}
                  aspectRatio={9 / 16}
                  h={imageHeight}
                  objectFit="cover"
                  autoPlay
                  muted
                  loop
                  controls
                />
              ) : (
                <Image
                  src={creative.verticalMediaUrl}
                  alt=""
                  aspectRatio={9 / 16}
                  h={imageHeight}
                  objectFit="cover"
                />
              )
            ) : (
              <Flex w="full" h="full" align="center" justify="center">
                <Text fontSize="xs" color="gray.400">
                  —
                </Text>
              </Flex>
            )}
          </Box>
        </Box>
      </Stack>

      <Box mb={4}>
        <HStack justify="space-between" align="flex-start" mb={1}>
          <Text fontSize="sm" fontWeight="semibold" color="gray.600">
            Primary Text
          </Text>
          <Button
            variant="link"
            fontSize="sm"
            fontWeight="medium"
            color="blue.600"
            onClick={onEdit}
            cursor="pointer"
            _hover={{ textDecoration: "underline" }}
            flexShrink={0}
            isDisabled={launching}
          >
            Edit
          </Button>
        </HStack>
        <Text fontSize="xs" color="gray.600" noOfLines={4}>
          {creative.primaryText || "—"}
        </Text>
      </Box>

      {[
        {
          icon: FaFacebookF,
          label: "FB Page",
          value:
            typeof creative.fbPage === "object" && creative.fbPage?.name != null
              ? creative.fbPage.name
              : "—",
        },
        {
          icon: FaInstagram,
          label: "IG Account",
          value:
            typeof creative.instagramAccount === "object" &&
            creative.instagramAccount?.username != null
              ? creative.instagramAccount.username
              : "—",
        },
      ].map(({ icon: IconComponent, label, value }) => (
        <HStack
          key={label}
          spacing={3}
          flex={1}
          align="center"
          justify="space-between"
          w="full"
          borderTop="1px solid"
          borderColor="gray.200"
          py="2"
        >
          <HStack display="flex" alignContent="center" gap={4}>
            <Box display="flex" alignContent="center" gap={2}>
              <Icon
                as={IconComponent}
                boxSize={4}
                color="gray.600"
                display="inline-block"
              />
              <Text
                fontSize="xs"
                color="gray.500"
                display="inline-block"
                textAlign="left"
                flex={1}
                width="70px"
              >
                {label}
              </Text>
            </Box>
            <Text
              fontSize="sm"
              fontWeight="medium"
              color="gray.800"
              noOfLines={1}
            >
              {value}
            </Text>
          </HStack>
          <Button
            variant="link"
            fontSize="sm"
            fontWeight="medium"
            color="blue.600"
            onClick={() => !launching && onEdit()}
            cursor="pointer"
            _hover={{ textDecoration: "underline" }}
            flexShrink={0}
            isDisabled={launching}
          >
            Change
          </Button>
        </HStack>
      ))}
    </Box>
  );
}
